import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { z } from 'zod';
import { Modal, Pill } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { ApiError, errorMessage } from '../../lib/api';
import { useStableCallback } from './hooks';
import { codeTone, copyText, fmtDateTime, fmtDay, fmtNum, fmtTime, relTime } from './format';
import { useApiKeyUsage, useApiRequests, useCreateApiKey, useRevokeApiKey, useRotateApiKey } from './queries';
import { API_SCOPES, SCOPE_LABELS_HE, type ApiKeyView, type OneTimeSecret } from './types';

const TOTP_RE = /^[0-9A-Za-z-]{6,20}$/;
const IP_RE = /^(\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?|[0-9a-fA-F:]+(\/\d{1,3})?)$/;

/** Form-level message for a failed create/rotate: STEP_UP_FAILED goes to the TOTP field. */
function stepUpError(e: unknown): { totp?: string; form?: string } {
  if (e instanceof ApiError && e.code === 'STEP_UP_FAILED') return { totp: e.message || 'קוד האימות שגוי או שפג תוקפו' };
  return { form: errorMessage(e) };
}

function TotpField(props: { id: string; error?: string; register: UseFormRegisterReturn }) {
  return (
    <div className="field">
      <label htmlFor={props.id}>קוד אימות (אפליקציית המאמת)</label>
      <input className="input mono" id={props.id} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" {...props.register} />
      <span className="help">פעולה רגישה. מזינים את הקוד הנוכחי מאפליקציית האימות שלכם.</span>
      {props.error ? <span className="err">{props.error}</span> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- create */

const CreateSchema = z.object({
  name: z.string().trim().min(1, 'חובה לתת שם למפתח').max(80, 'עד 80 תווים'),
  scopes: z.array(z.enum(API_SCOPES)).min(1, 'יש לבחור לפחות הרשאה אחת'),
  dailyQuota: z.coerce.number<string>().int('מספר שלם').min(1, 'לפחות 1').max(100_000, 'עד 100,000'),
  ratePerMin: z.coerce.number<string>().int('מספר שלם').min(1, 'לפחות 1').max(600, 'עד 600'),
  mode: z.enum(['live', 'test']),
  ipAllowlist: z.string().refine(
    (s) => splitIps(s).every((x) => IP_RE.test(x)),
    'כתובת IP או טווח CIDR לא תקינים',
  ).refine((s) => splitIps(s).length <= 50, 'עד 50 כתובות'),
  expiresAt: z.string(),
  totpCode: z.string().trim().regex(TOTP_RE, 'קוד אימות לא תקין'),
});
type CreateForm = z.input<typeof CreateSchema>;
type CreateOut = z.output<typeof CreateSchema>;

function splitIps(s: string): string[] {
  return s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function CreateKeyModal(props: { open: boolean; onClose: () => void; onCreated: (s: OneTimeSecret) => void }) {
  const create = useCreateApiKey();
  const [formError, setFormError] = useState<string>();
  const form = useForm<CreateForm, unknown, CreateOut>({
    resolver: zodResolver(CreateSchema),
    defaultValues: {
      name: '',
      scopes: ['messages:send', 'messages:read'],
      dailyQuota: '200',
      ratePerMin: '60',
      mode: 'live',
      ipAllowlist: '',
      expiresAt: '',
      totpCode: '',
    },
  });
  const { register, handleSubmit, formState, setError, reset } = form;
  const errs = formState.errors;

  const close = useStableCallback(() => {
    reset();
    setFormError(undefined);
    create.reset();
    props.onClose();
  });

  const onSubmit = handleSubmit((v) => {
    setFormError(undefined);
    create.mutate(
      {
        name: v.name,
        isTest: v.mode === 'test',
        scopes: v.scopes,
        dailyQuota: v.dailyQuota,
        ratePerMin: v.ratePerMin,
        ipAllowlist: splitIps(v.ipAllowlist),
        expiresAt: v.expiresAt ? new Date(`${v.expiresAt}T23:59:59`).toISOString() : null,
        totpCode: v.totpCode,
      },
      {
        onSuccess: (r) => {
          toast(`המפתח "${r.apiKey.name}" נוצר`);
          reset();
          props.onCreated({ title: 'המפתח נוצר', keyName: r.apiKey.name, key: r.key, callbackSecret: r.callbackSecret });
        },
        onError: (e) => {
          const m = stepUpError(e);
          if (m.totp) setError('totpCode', { message: m.totp });
          else setFormError(m.form);
          toast(m.totp ?? m.form ?? 'יצירת המפתח נכשלה', 'error');
        },
      },
    );
  });

  return (
    <Modal
      open={props.open}
      title="מפתח API חדש"
      onClose={close}
      wide
      footer={
        <>
          <button type="submit" form="k-create" className="btn primary" disabled={create.isPending}>
            {create.isPending ? 'יוצר…' : 'יצירת מפתח'}
          </button>
          <button type="button" className="btn" onClick={close}>
            ביטול
          </button>
        </>
      }
    >
      <form id="k-create" className="stack" onSubmit={onSubmit} noValidate>
        {formError ? (
          <div className="callout danger">
            <Icon name="alert" />
            <div>{formError}</div>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="k-name">שם (למי המפתח)</label>
          <input className="input" id="k-name" placeholder="למשל: פורטל" {...register('name')} />
          {errs.name ? <span className="err">{errs.name.message}</span> : null}
        </div>
        <div className="field">
          <span className="label">הרשאות</span>
          {API_SCOPES.map((s) => (
            <label key={s} className="check">
              <input type="checkbox" value={s} {...register('scopes')} /> {SCOPE_LABELS_HE[s]}{' '}
              <code className="ltr small muted">{s}</code>
            </label>
          ))}
          {errs.scopes ? <span className="err">{errs.scopes.message}</span> : null}
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="k-quota">מכסה יומית</label>
            <input className="input" id="k-quota" inputMode="numeric" {...register('dailyQuota')} />
            <span className="help">הודעות ביום. עד 100,000</span>
            {errs.dailyQuota ? <span className="err">{errs.dailyQuota.message}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="k-rate">קצב בקשות לדקה</label>
            <input className="input" id="k-rate" inputMode="numeric" {...register('ratePerMin')} />
            <span className="help">כל הבקשות של המפתח. עד 600</span>
            {errs.ratePerMin ? <span className="err">{errs.ratePerMin.message}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="k-mode">סוג</label>
            <select className="select" id="k-mode" {...register('mode')}>
              <option value="live">אמיתי</option>
              <option value="test">בדיקות (שולח רק לקבוצת הבדיקה)</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="k-exp">תוקף (לא חובה)</label>
            <input className="input" id="k-exp" type="date" {...register('expiresAt')} />
            <span className="help">ריק = ללא תאריך תפוגה</span>
          </div>
          <div className="field full">
            <label htmlFor="k-ips">כתובות IP מורשות (לא חובה)</label>
            <textarea className="textarea mono" id="k-ips" rows={2} placeholder="203.0.113.10&#10;198.51.100.0/24" {...register('ipAllowlist')} />
            <span className="help">כתובת או טווח CIDR בכל שורה. ריק = מכל כתובת</span>
            {errs.ipAllowlist ? <span className="err">{errs.ipAllowlist.message}</span> : null}
          </div>
        </div>
        <TotpField id="k-totp" error={errs.totpCode?.message} register={register('totpCode')} />
      </form>
    </Modal>
  );
}

/* ---------------------------------------------------------- shown once */

export function SecretModal(props: { secret: OneTimeSecret | null; onClose: () => void }) {
  const s = props.secret;
  const onClose = useStableCallback(props.onClose);
  return (
    <Modal
      open={Boolean(s)}
      title={s?.title ?? ''}
      onClose={onClose}
      footer={
        <button className="btn primary" onClick={onClose}>
          שמרתי, סגירה
        </button>
      }
    >
      {s ? (
        <>
          <p className="small muted">
            מעתיקים עכשיו ושומרים במשתני הסביבה של המערכת ששולחת. אחרי שסוגרים, אי אפשר לראות אותו שוב.
          </p>
          <div className="field mt">
            <label htmlFor="s-key">מפתח API ({s.keyName})</label>
            <div className="input-group">
              <input className="input mono" id="s-key" readOnly value={s.key} onFocus={(e) => e.currentTarget.select()} />
              <button className="btn" onClick={() => void copyText(s.key)}>
                <Icon name="copy" />
                העתקה
              </button>
            </div>
          </div>
          {s.callbackSecret ? (
            <div className="field mt">
              <label htmlFor="s-cb">סוד לאימות callbackUrl</label>
              <div className="input-group">
                <input className="input mono" id="s-cb" readOnly value={s.callbackSecret} onFocus={(e) => e.currentTarget.select()} />
                <button className="btn" onClick={() => void copyText(s.callbackSecret!)}>
                  <Icon name="copy" />
                  העתקה
                </button>
              </div>
              <span className="help">משמש לאימות החתימה של עדכוני הסטטוס שנשלחים ל-callbackUrl. גם הוא מוצג פעם אחת בלבד.</span>
            </div>
          ) : null}
          {s.previousExpiresAt ? (
            <div className="callout warn mt">
              <Icon name="clock" />
              <div>המפתח הקודם ימשיך לעבוד עד {fmtDateTime(s.previousExpiresAt)}. מחליפים אותו במערכת ששולחת לפני כן.</div>
            </div>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

/* --------------------------------------------------------------- rotate */

const RotateSchema = z.object({ totpCode: z.string().trim().regex(TOTP_RE, 'קוד אימות לא תקין') });

export function RotateKeyModal(props: { apiKey: ApiKeyView | null; onClose: () => void; onRotated: (s: OneTimeSecret) => void }) {
  const rotate = useRotateApiKey();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit, formState, setError, reset } = useForm<{ totpCode: string }>({
    resolver: zodResolver(RotateSchema),
    defaultValues: { totpCode: '' },
  });
  const k = props.apiKey;
  const close = useStableCallback(() => {
    reset();
    setFormError(undefined);
    props.onClose();
  });
  const onSubmit = handleSubmit((v) => {
    if (!k) return;
    setFormError(undefined);
    rotate.mutate(
      { id: k.id, totpCode: v.totpCode },
      {
        onSuccess: (r) => {
          toast(`נוצר מפתח חדש ל${k.name}. הישן ימשיך לעבוד 7 ימים`);
          reset();
          props.onRotated({ title: 'המפתח החדש נוצר', keyName: r.apiKey.name, key: r.key, previousExpiresAt: r.previous.expiresAt });
        },
        onError: (e) => {
          const m = stepUpError(e);
          if (m.totp) setError('totpCode', { message: m.totp });
          else setFormError(m.form);
          toast(m.totp ?? m.form ?? 'הרוטציה נכשלה', 'error');
        },
      },
    );
  });
  return (
    <Modal
      open={Boolean(k)}
      title={`רוטציה למפתח ${k?.name ?? ''}`}
      onClose={close}
      footer={
        <>
          <button type="submit" form="k-rotate" className="btn primary" disabled={rotate.isPending}>
            {rotate.isPending ? 'יוצר…' : 'יצירת מפתח חדש'}
          </button>
          <button type="button" className="btn" onClick={close}>
            ביטול
          </button>
        </>
      }
    >
      <form id="k-rotate" className="stack" onSubmit={onSubmit} noValidate>
        <p className="small">
          נוצר מפתח חדש עם אותן הרשאות ומכסה. המפתח הנוכחי (<code className="ltr">{k?.prefix}••••</code>) ימשיך לעבוד 7 ימים, ואז יפסיק.
        </p>
        {formError ? (
          <div className="callout danger">
            <Icon name="alert" />
            <div>{formError}</div>
          </div>
        ) : null}
        <TotpField id="r-totp" error={formState.errors.totpCode?.message} register={register('totpCode')} />
      </form>
    </Modal>
  );
}

/* --------------------------------------------------------------- revoke */

export function RevokeKeyModal(props: { apiKey: ApiKeyView | null; onClose: () => void }) {
  const revoke = useRevokeApiKey();
  const k = props.apiKey;
  const onClose = useStableCallback(props.onClose);
  return (
    <Modal
      open={Boolean(k)}
      title={`ביטול המפתח ${k?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <button
            className="btn danger"
            disabled={revoke.isPending}
            onClick={() =>
              k &&
              revoke.mutate(k.id, {
                onSuccess: () => {
                  toast(`המפתח של ${k.name} בוטל`);
                  props.onClose();
                },
                onError: (e) => toast(errorMessage(e), 'error'),
              })
            }
          >
            {revoke.isPending ? 'מבטל…' : 'ביטול המפתח'}
          </button>
          <button className="btn" onClick={onClose}>
            חזרה
          </button>
        </>
      }
    >
      <div className="callout danger">
        <Icon name="alert" />
        <div>
          המפתח <code className="ltr">{k?.prefix}••••</code> יפסיק לעבוד מיד. כל בקשה איתו תיענה ב-401, וה-Webhooks שלו לא יקבלו עוד עדכונים. אי
          אפשר לבטל את הפעולה.
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- usage */

export function UsageModal(props: { apiKey: ApiKeyView | null; onClose: () => void }) {
  const k = props.apiKey;
  const onClose = useStableCallback(props.onClose);
  const usage = useApiKeyUsage(k?.id);
  const reqs = useApiRequests(k?.id, 20);
  const u = usage.data;
  const max = Math.max(1, ...(u?.last7Days.map((d) => d.total) ?? [1]));
  const errorsToday = u ? Object.entries(u.today.byStatus).reduce((a, [code, n]) => (Number(code) >= 400 ? a + n : a), 0) : 0;
  return (
    <Modal open={Boolean(k)} title={`שימוש: ${k?.name ?? ''}`} onClose={onClose} wide>
      {usage.isLoading ? (
        <div className="stack" aria-busy="true">
          <div className="skel" style={{ height: 86 }} />
          <div className="skel" style={{ height: 180 }} />
        </div>
      ) : usage.isError || !u ? (
        <div className="callout danger">
          <Icon name="alert" />
          <div>{errorMessage(usage.error)}</div>
        </div>
      ) : (
        <div className="stack-lg">
          <div className="stat-strip">
            <div className="stat">
              <div className="label">בקשות היום</div>
              <div className="value num">{fmtNum(u.today.total)}</div>
            </div>
            <div className="stat">
              <div className="label">מכסה היום</div>
              <div className="value num">{fmtNum(u.quotaUsedToday)}</div>
              <div className="sub">מתוך {fmtNum(u.dailyQuota)}</div>
            </div>
            <div className="stat">
              <div className="label">שגיאות היום</div>
              <div className="value num">{fmtNum(errorsToday)}</div>
            </div>
            <div className="stat">
              <div className="label">שימוש אחרון</div>
              <div className="value" style={{ fontSize: 18 }}>
                {relTime(u.lastUsedAt)}
              </div>
            </div>
          </div>
          {Object.keys(u.today.byStatus).length ? (
            <div className="row">
              <span className="small muted">תשובות היום:</span>
              {Object.entries(u.today.byStatus)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([code, n]) => (
                  <Pill key={code} tone={codeTone(Number(code))}>
                    {code} × {fmtNum(n)}
                  </Pill>
                ))}
            </div>
          ) : null}
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>יום</th>
                  <th>בקשות</th>
                  <th>שגיאות</th>
                  <th className="hide-sm">
                    <span className="sr-only">גרף</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {u.last7Days.map((d) => (
                  <tr key={d.day}>
                    <td>{fmtDay(d.day)}</td>
                    <td className="num">{fmtNum(d.total)}</td>
                    <td className="num">{d.errors ? <span className="pill danger">{fmtNum(d.errors)}</span> : '0'}</td>
                    <td className="hide-sm" style={{ width: '40%' }}>
                      <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(d.total / max) * 100}%`, background: 'var(--accent)' }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 className="small strong">בקשות אחרונות</h3>
            {reqs.isLoading ? (
              <div className="skel" style={{ height: 80 }} />
            ) : !reqs.data?.length ? (
              <p className="small muted">אין עדיין בקשות עם המפתח הזה.</p>
            ) : (
              <div className="table-wrap">
                <table className="t">
                  <tbody>
                    {reqs.data.map((c) => (
                      <tr key={c.id}>
                        <td className="num">{fmtTime(c.at)}</td>
                        <td>
                          <code className="ltr">
                            {c.method} {c.path}
                          </code>
                        </td>
                        <td>
                          <span className={`pill ${codeTone(c.statusCode)}`}>{c.statusCode}</span>
                        </td>
                        <td className="hide-sm small muted">{c.note ?? c.errorCode ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {k && (k.ipAllowlist.length || k.expiresAt) ? (
            <div className="small muted">
              {k.ipAllowlist.length ? (
                <div>
                  כתובות מורשות: <span className="ltr">{k.ipAllowlist.join(', ')}</span>
                </div>
              ) : null}
              {k.expiresAt ? <div>תוקף עד {fmtDateTime(k.expiresAt)}</div> : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
